import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Search, Shield, Star, Sparkles, Users, Briefcase, MapPin, ChevronRight, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { api } from '@/lib/api';
import { useLang } from '@/lib/i18n';
import { CategoryTile } from '@/components/CategoryTile';
import { JobeurCard } from '@/components/JobeurCard';
import { Logo } from '@/components/Logo';
import { UserBadges, TrustBadge } from '@/components/TrustBadge';

const TESTIMONIALS = [
  { name: 'Grace K.', role: 'Cliente, Ngaliema', text: 'J’ai trouvé un plombier sérieux en 20 minutes. Paiement sécurisé, tout était clair.', rating: 5 },
  { name: 'Patrick M.', role: 'Plombier, Gombe', text: 'Grâce à Job Kongo, je travaille toute la semaine. Les commissions sont transparentes.', rating: 5 },
  { name: 'Esther T.', role: 'Prof. de maths, Kalamu', text: 'Les clients me font confiance grâce aux badges. Je reçois des avis vérifiés.', rating: 5 },
];

export default function HomePage() {
  const nav = useNavigate();
  const { t } = useLang();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [urgent, setUrgent] = useState([]);
  const [stats, setStats] = useState({ jobeurs_verified: 0, jobs_completed: 0, communes: 0 });
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [city, setCity] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/catalog/categories'),
      api.get('/catalog/cities'),
      api.get('/services/featured?limit=6'),
      api.get('/requests/urgent?limit=4'),
      api.get('/catalog/stats-public'),
    ]).then(([c, ci, f, u, s]) => {
      setCategories(c.data.items || []);
      setCities(ci.data.items || []);
      setFeatured(f.data.items || []);
      setUrgent(u.data.items || []);
      setStats(s.data || {});
    }).catch(() => {});
  }, []);

  const goSearch = () => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (cat) p.set('category', cat);
    if (city) p.set('city', city);
    nav(`/search?${p.toString()}`);
  };

  return (
    <div className="pb-8">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-sky pointer-events-none" />
        <div className="absolute inset-0 bg-noise opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 pt-10 md:pt-16 pb-8">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 bg-white border border-[#C7E2FF] rounded-full px-3 py-1.5 text-xs font-medium text-[#0E3F80] shadow-sm">
              <Shield size={14} className="text-[#1F9D63]" /> {t('home.badge')}
            </div>
            <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight text-[#0B1220] leading-[1.05]">
              {t('home.title.1')} <span className="text-[#1677F2]">jobeur</span>
              <br className="hidden md:block" />
              {t('home.title.2')}
            </h1>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl">
              {t('home.subtitle')} <span className="font-semibold text-[#16764A]">Mosala na confiance.</span>
            </p>
          </motion.div>

          {/* Search bar */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
            <Card className="mt-6 md:mt-8 p-3 border-[#C7E2FF] shadow-[0_10px_24px_rgba(11,18,32,0.08)]">
              <div className="flex flex-col md:flex-row gap-2">
                <div className="flex-1 flex items-center gap-2 bg-[#F7FAFF] rounded-[var(--radius)] px-3 h-12">
                  <Search size={18} className="text-slate-500" />
                  <Input
                    placeholder={t('home.search.placeholder')}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="border-0 bg-transparent focus-visible:ring-0 px-0 h-full"
                    data-testid="home-hero-search-query"
                    onKeyDown={(e) => e.key === 'Enter' && goSearch()}
                  />
                </div>
                <select
                  value={cat}
                  onChange={(e) => setCat(e.target.value)}
                  className="h-12 px-3 rounded-[var(--radius)] bg-[#F7FAFF] border-0 text-sm md:w-52 focus:ring-2 focus:ring-[#1677F2]/20"
                  data-testid="home-hero-search-category"
                >
                  <option value="">{t('home.search.allCategories')}</option>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                </select>
                <select
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="h-12 px-3 rounded-[var(--radius)] bg-[#F7FAFF] border-0 text-sm md:w-44 focus:ring-2 focus:ring-[#1677F2]/20"
                  data-testid="home-hero-search-city"
                >
                  <option value="">{t('home.search.allCities')}</option>
                  {cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
                <Button
                  onClick={goSearch}
                  size="lg"
                  className="bg-[#1677F2] hover:bg-[#0F5FCC] h-12 px-6"
                  data-testid="home-hero-search-button"
                >
                  {t('home.search.button')} <ArrowRight size={16} className="ml-1" />
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Trust bar */}
          <div className="mt-6 grid grid-cols-3 gap-3 md:gap-6">
            <div className="text-center md:text-left">
              <p className="text-2xl md:text-3xl font-bold text-[#0E3F80] font-mono-num">{stats.jobeurs_verified || 1}+</p>
              <p className="text-xs text-slate-600">{t('home.stats.jobeurs')}</p>
            </div>
            <div className="text-center md:text-left">
              <p className="text-2xl md:text-3xl font-bold text-[#1F9D63] font-mono-num">{stats.jobs_completed || 0}+</p>
              <p className="text-xs text-slate-600">{t('home.stats.jobs')}</p>
            </div>
            <div className="text-center md:text-left">
              <p className="text-2xl md:text-3xl font-bold text-[#C9A800] font-mono-num">{stats.communes || 0}+</p>
              <p className="text-xs text-slate-600">{t('home.stats.communes')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 mt-8">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0B1220]">{t('home.categories.title')}</h2>
            <p className="text-sm text-slate-600">{t('home.categories.subtitle')}</p>
          </div>
          <Link to="/search" className="hidden md:inline-flex text-sm font-medium text-[#1677F2] items-center gap-1">
            {t('home.seeAll')} <ChevronRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          {categories.slice(0, 14).map((c) => <CategoryTile key={c.slug} category={c} />)}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 mt-14">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0B1220]">{t('home.how.title')}</h2>
          <p className="text-sm text-slate-600 mt-1">{t('home.how.subtitle')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[{
            icon: Briefcase, title: t('home.how.1.title'), desc: t('home.how.1.desc'), color: 'bg-[#EAF3FF] text-[#1677F2]'
          }, {
            icon: Users, title: t('home.how.2.title'), desc: t('home.how.2.desc'), color: 'bg-[#DFF7EA] text-[#16764A]'
          }, {
            icon: Shield, title: t('home.how.3.title'), desc: t('home.how.3.desc'), color: 'bg-[#FFF6C7] text-[#7A5F00]'
          }].map((s) => (
            <Card key={s.title} className="p-5 border-[#E2E8F0] bg-white">
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center mb-3 ${s.color}`}>
                <s.icon size={22} />
              </div>
              <h3 className="font-semibold text-lg text-[#0B1220]">{s.title}</h3>
              <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Jobs urgents */}
      {urgent.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 mt-14">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0B1220] flex items-center gap-2">
                <Zap size={22} className="text-[#CE1021]" /> {t('home.urgent.title')}
              </h2>
              <p className="text-sm text-slate-600">{t('home.urgent.subtitle')}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {urgent.map((r) => (
              <Card key={r.id} className="p-4 border-[#FFD7DB] bg-[#FFF5F6]">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="inline-block text-[10px] font-bold bg-[#CE1021] text-white px-2 py-0.5 rounded-full uppercase tracking-wider">Urgent</span>
                    <h3 className="font-semibold mt-1 truncate">{r.title}</h3>
                    <p className="text-xs text-slate-600 flex items-center gap-1 mt-0.5">
                      <MapPin size={11} /> {r.commune}, {r.city}
                    </p>
                    <p className="text-sm text-slate-700 mt-2 line-clamp-2">{r.description}</p>
                  </div>
                  <Link to={`/request/${r.id}`} className="text-[#1677F2] text-sm font-medium flex items-center gap-1 flex-shrink-0">
                    {t('home.urgent.see')} <ChevronRight size={14} />
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Featured jobeurs */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 mt-14">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0B1220] flex items-center gap-2">
                <Sparkles size={22} className="text-[#C9A800]" /> {t('home.featured.title')}
              </h2>
              <p className="text-sm text-slate-600">{t('home.featured.subtitle')}</p>
            </div>
            <Link to="/search" className="hidden md:inline-flex text-sm font-medium text-[#1677F2] items-center gap-1">{t('home.seeAll')} <ChevronRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {featured.map((s) => (
              <JobeurCard
                key={s.id}
                jobeur={{
                  id: s.jobeur_id,
                  first_name: s.jobeur_name?.split(' ')[0] || '',
                  last_name: s.jobeur_name?.split(' ').slice(1).join(' ') || '',
                  photo_base64: s.jobeur_photo,
                  city: s.jobeur_city,
                  commune: s.jobeur_commune,
                  rating_avg: s.jobeur_rating_avg,
                  rating_count: s.jobeur_rating_count,
                  phone_verified: s.jobeur_phone_verified,
                  id_verified: s.jobeur_id_verified,
                }}
                service={s}
              />
            ))}
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 mt-14">
        <h2 className="text-2xl md:text-3xl font-bold text-[#0B1220] text-center mb-6">{t('home.testimonials.title')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t) => (
            <Card key={t.name} className="p-5 border-[#E2E8F0] bg-white">
              <div className="flex gap-0.5 text-[#F7D618] mb-2">
                {Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={14} className="fill-[#F7D618]" />)}
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">“{t.text}”</p>
              <div className="mt-4 flex items-center gap-3">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-[#EAF3FF] text-[#0E3F80] text-xs font-semibold">{t.name.split(' ').map((x) => x[0]).join('')}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-semibold">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
        <Card className="p-6 md:p-10 bg-gradient-to-br from-[#1677F2] to-[#0E3F80] border-0 text-white overflow-hidden relative">
          <div className="absolute inset-0 bg-noise opacity-30" />
          <div className="relative grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h2 className="text-2xl md:text-4xl font-bold">{t('home.cta.title')}</h2>
              <p className="mt-3 text-white/90">
                {t('home.cta.subtitle')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
              <Button asChild size="lg" className="bg-white text-[#0E3F80] hover:bg-[#F2F8FF]" data-testid="home-cta-client">
                <Link to="/signup?role=employeur">{t('home.cta.client')}</Link>
              </Button>
              <Button asChild size="lg" className="bg-[#F7D618] text-[#0B1220] hover:bg-[#FFE36B]" data-testid="home-cta-jobeur">
                <Link to="/signup?role=jobeur">{t('home.cta.jobeur')}</Link>
              </Button>
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
}
